import Papa from 'papaparse'
import type { CatalogRow, ColumnMapping } from '../domain/catalog'
import { formatMoney } from '../domain/pricing'

const sanitizeSpreadsheetText = (value: string): string => {
  if (/^[=+@]/.test(value) || (/^-/.test(value) && !/^-\d+(?:[.,]\d+)?$/.test(value))) return `'${value}`
  return value
}

export function getExportableRows(rows: CatalogRow[], includedWarningRows: ReadonlySet<number>): CatalogRow[] {
  return rows.filter(
    (row) => row.status === 'valid' || (row.status === 'warning' && includedWarningRows.has(row.sourceRowNumber)),
  )
}

export function buildWooCommerceCsv(rows: CatalogRow[], mapping: ColumnMapping): string {
  const validRows = rows.filter((row) => row.status !== 'excluded')
  const output = validRows.map((row) => {
    const outputRow: Record<string, string | number> = {
      SKU: sanitizeSpreadsheetText(row.sku),
      Name: sanitizeSpreadsheetText(row.name),
      'Regular price': formatMoney(row.regularPriceCents),
    }

    if (mapping.stock && row.stock !== null) {
      outputRow['In stock?'] = row.stock > 0 ? 1 : 0
      outputRow.Stock = row.stock
    }
    if (mapping.category) outputRow.Categories = sanitizeSpreadsheetText(row.category)
    if (mapping.image) outputRow.Images = sanitizeSpreadsheetText(row.imageUrl)

    return outputRow
  })

  return `\uFEFF${Papa.unparse(output, { newline: '\r\n' })}`
}

export function downloadTextFile(contents: string, filename: string, type = 'text/csv;charset=utf-8'): void {
  const blob = new Blob([contents], { type })
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  anchor.click()
  URL.revokeObjectURL(url)
}

export function buildIssueReport(rows: CatalogRow[]): string {
  const issueRows = rows.flatMap((row) =>
    row.issues.map((issue) => ({
      'Source row': row.sourceRowNumber,
      SKU: sanitizeSpreadsheetText(row.sku),
      Name: sanitizeSpreadsheetText(row.name),
      Severity: issue.severity,
      Code: issue.code,
      Message: issue.message,
    })),
  )
  return `\uFEFF${Papa.unparse(issueRows, { newline: '\r\n' })}`
}
